import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Box,
  Button,
  Flex,
  Link,
  Loading,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import get from 'lodash-es/get';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useMatches, useParams } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import type { ProductFilterKeys } from '@jane/search/types';
import {
  buildSearchFilter,
  facetToFilter,
  handleFilterChange,
  handleFilterDeselect,
  mapCurrentSortToSearchSort,
  updateFacetCounts,
} from '@jane/search/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
import type { Store, StoreSpecial } from '@jane/shared/types';
import { normalizeString } from '@jane/shared/util';

import {
  FilterCheckbox,
  Modal,
  ModalHeader,
  NoResults,
  SearchInputContainer,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import { searchOptions } from '../../data-access';
import {
  useKioskSpacing,
  useRowContext,
  useTypedRouteLoaderData,
} from '../../hooks';
import {
  useBrandSpecials,
  useProductSort,
  useSearchContext,
} from '../../providers';
import type { OptionalMenuRouteParams } from '../../types';
import {
  byCustomBrandOrder,
  getSource,
  isFilterSelected,
  isSupportedStoreSpecial,
  trackFilteredProductList,
} from '../../util';

export const BrandFilters = () => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { category, specialId, brandSpecialId } =
    useParams<OptionalMenuRouteParams>();
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { optional_filters } = store;
  const { currentSort } = useProductSort({
    defaultSortName: null,
    optionalFilters: store.optional_filters,
    store,
  });

  const { brandSpecials, brandSpecialFilter } = useBrandSpecials();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special)
  );
  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const brandSpecial = brandSpecials.find(
    ({ id }) => `${id}` === brandSpecialId
  );

  const [brandSearchTerm, setBrandSearchTerm] = useState('');

  const { pathname } = useLocation();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { searchState, setSearchState } = useSearchContext();

  const { featuredFilter } = useRowContext(store);
  const isGold = category === 'jane-gold';

  const options = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
    storeSpecial: specialInScope,
    brandSpecial,
  });

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: options.staticFilters,
  });

  const searchFilterWithoutBrands = buildSearchFilter({
    searchState: {
      ...searchState,
      filters: { ...searchState.filters, brand: [] },
    },
    staticFilters: options.staticFilters,
  });

  const searchSort = mapCurrentSortToSearchSort(currentSort);

  const {
    searchResultFacets: initialResultsFacets,
    numHits: totalProductCount,
    isLoading: initialResultsFacetsLoading,
  } = useRecommendedSort({
    maxProducts: -1,
    searchAttributes: ['*'],
    searchFilter: searchFilterWithoutBrands,
    searchSort,
    storeId: store.id,
  });

  const {
    searchResultFacets: resultsFacets,
    isLoading: resultsFacetsLoading,
    numHits: filteredProductCount,
    isSuccess: resultsFacetsSuccess,
  } = useRecommendedSort({
    maxProducts: -2,
    searchAttributes: ['*'],
    searchFilter,
    searchSort,
    storeId: store.id,
  });

  const presentedFacets = useMemo(
    () => updateFacetCounts(searchState, initialResultsFacets, resultsFacets),
    [searchState, initialResultsFacets, resultsFacets]
  );

  const brands = facetToFilter(presentedFacets['brand'] ?? {}, {
    filterGroup: 'Brand',
  })
    .sort(byCustomBrandOrder(get(optional_filters, 'brand')))
    .filter(
      ({ count, value }) =>
        normalizeString(value.toLowerCase()).includes(
          normalizeString(brandSearchTerm.toLowerCase())
        ) && count > 0
    );

  const onChange = useCallback(
    (filterKey: ProductFilterKeys, filterValue: string) => {
      return setSearchState(
        handleFilterChange(filterKey, filterValue, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const onDeselect = useCallback(
    (filterKey: ProductFilterKeys) => {
      return setSearchState(handleFilterDeselect(filterKey, searchState));
    },
    [searchState, setSearchState]
  );

  const isLoading = initialResultsFacetsLoading || resultsFacetsLoading;

  const noResults = !isLoading && brands.length === 0 && resultsFacetsSuccess;

  const onClearAll = () =>
    setSearchState(handleFilterDeselect('all', searchState));

  const matches = useMatches();

  const onShowFilteredProducts = useCallback(() => {
    trackFilteredProductList({
      dealName: specialInScope ? [specialInScope.title] : null,
      filterOptions: searchState,
      filteredProductCount,
      source: getSource({
        currentSpecial: specialInScope,
        isTopper: false,
        matches,
        pathname,
      }),
      totalProductCount,
    });
  }, [
    filteredProductCount,
    matches,
    pathname,
    searchState,
    specialInScope,
    totalProductCount,
  ]);

  return (
    <Modal
      appId="root"
      onRequestClose={() => goBack()}
      open
      overlayClose
      variant="standard"
    >
      <ModalHeader nested onRequestClose={() => goBack()}>
        <Flex alignItems="center" grow={1} justifyContent="space-between">
          <Typography mr={96} variant={isIpad ? 'header-bold' : 'title-bold'}>
            Filter by brand
          </Typography>
          <Flex>
            <Button
              label="Clear all"
              onClick={() => onClearAll()}
              size={isIpad ? 'small' : 'default'}
              variant="tertiary"
            />
            <Button
              loading={isLoading}
              ml={16}
              label={`Show ${pluralize({
                noun: 'product',
                number: filteredProductCount,
              })}`}
              size={isIpad ? 'small' : 'default'}
              onClick={onShowFilteredProducts}
              to={pathname.replace(/\/filters\/brand$/, '')}
            />
          </Flex>
        </Flex>
      </ModalHeader>
      <Box background="grays-ultralight" height={16} width="100%" />
      <Modal.Content padding={false}>
        <Flex
          flexDirection="column"
          px={getKioskSpacing(32, 40)}
          py={getKioskSpacing(32, 40)}
        >
          <Flex
            alignItems="center"
            mb={16}
            grow={1}
            justifyContent="space-between"
          >
            <Typography variant="header-bold">Brands</Typography>
            <Link onClick={() => onDeselect('brand')}>
              <Typography>Deselect all</Typography>
            </Link>
          </Flex>

          <SearchInputContainer>
            <SearchField
              debounceDelay={300}
              defaultValue={brandSearchTerm}
              enterKeyHint="search"
              isDebounced
              label="Search brands"
              labelHidden
              mb={getKioskSpacing(16, 24)}
              name="brand-search"
              onChange={setBrandSearchTerm}
              placeholder="Search for a brand"
            />
          </SearchInputContainer>

          <Flex flexDirection="column" grow={1}>
            {isLoading ? (
              <Flex alignItems="center" height="100%" justifyContent="center">
                <Loading color="black" size="lg" />
              </Flex>
            ) : (
              <>
                {brands.length > 0 &&
                  brands.map((brand) => {
                    const checked = isFilterSelected(
                      searchState,
                      'brand',
                      brand
                    );
                    return (
                      <FilterCheckbox
                        checked={checked}
                        item={brand}
                        key={brand.value}
                        onChange={(isChecked) => {
                          if (checked !== isChecked) {
                            onChange('brand', brand.value);
                          }
                        }}
                      />
                    );
                  })}
              </>
            )}

            {noResults && (
              <NoResults body="Looks like no brands match your search. Please try again." />
            )}
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
